<template>
  <div class="p-6">
    <!-- 页面标题和操作按钮 -->
    <div class="flex justify-between items-center mb-6">
      <div>
        <h1 class="text-2xl font-bold text-gray-900 dark:text-gray-100">兑换码管理</h1>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">管理兑换码批次和导出</p>
      </div>
      <button @click="openCreateModal" class="primary-btn gap-2">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
        </svg>
        创建批次
      </button>
    </div>

    <!-- 批次列表 -->
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900">
      <!-- 加载状态 -->
      <div v-if="loading" class="p-8 text-center">
        <svg class="animate-spin h-8 w-8 mx-auto text-indigo-600" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
        </svg>
        <p class="mt-2 text-gray-500 dark:text-gray-400">加载中...</p>
      </div>

      <!-- 数据表格 -->
      <table v-else class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead class="bg-gray-50 dark:bg-gray-900">
          <tr>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">批次号</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">价格方案</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">数量</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">已兑/已用</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">过期时间</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">状态</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">创建时间</th>
            <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">操作</th>
          </tr>
        </thead>
        <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
          <tr v-for="batch in batches" :key="batch.id" class="hover:bg-gray-50 dark:hover:bg-gray-700">
            <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
              {{ batch.batchNo }}
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
              <div class="text-sm text-gray-900 dark:text-gray-100">{{ getPricingTitle(batch.pricingId) }}</div>
              <div v-if="getPricingSubtitle(batch.pricingId)" class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {{ getPricingSubtitle(batch.pricingId) }}
              </div>
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
              {{ batch.generatedCount }} / {{ batch.totalCount }}
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
              {{ batchStats[batch.id]?.redeemed || 0 }} / {{ batch.generatedCount }}
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
              {{ batch.expiresAt ? formatDate(batch.expiresAt) : '-' }}
            </td>
            <td class="px-6 py-4 whitespace-nowrap">
              <span :class="batch.status === 'ACTIVE' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'" class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full">
                {{ batch.status === 'ACTIVE' ? '激活' : '禁用' }}
              </span>
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
              {{ formatDate(batch.createdAt) }}
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
              <button @click="viewBatchDetail(batch)" class="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300 mr-3">
                详情
              </button>
              <button @click="exportBatch(batch, 'csv')" class="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300 mr-3">
                导出CSV
              </button>
              <button @click="exportBatch(batch, 'txt')" class="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300">
                导出TXT
              </button>
            </td>
          </tr>
          <tr v-if="batches.length === 0">
            <td colspan="8" class="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
              暂无数据
            </td>
          </tr>
        </tbody>
      </table>

      <!-- 分页 -->
      <div v-if="total > 0" class="px-6 py-4 flex items-center justify-between border-t border-gray-200 dark:border-gray-700">
        <div class="text-sm text-gray-500 dark:text-gray-400">
          共 {{ total }} 条记录
        </div>
        <div class="flex gap-2">
          <button @click="loadBatches(page - 1)" :disabled="page === 0" class="px-3 py-1 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed">
            上一页
          </button>
          <button @click="loadBatches(page + 1)" :disabled="(page + 1) * pageSize >= total" class="px-3 py-1 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed">
            下一页
          </button>
        </div>
      </div>
    </div>

    <!-- 创建批次 Modal -->
    <Modal
      :show="showCreateModal"
      @close="showCreateModal = false"
      modalSize="sm:my-8 sm:w-full sm:max-w-2xl sm:p-6"
    >
      <template #header>
        创建兑换码批次
      </template>
      <template #body>
        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">套餐与时长</label>
            <select
              v-model.number="createForm.pricingId"
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
            >
              <option :value="0">请选择套餐与时长</option>
              <option
                v-for="option in selectablePricingOptions"
                :key="option.pricingId"
                :value="option.pricingId"
              >
                {{ formatPricingOptionLabel(option) }}
              </option>
            </select>
            <p v-if="selectablePricingOptions.length > 0" class="mt-1 text-xs text-gray-500 dark:text-gray-400">
              仅显示已启用且未隐藏的价格方案
            </p>
            <p v-else class="mt-1 text-xs text-amber-600 dark:text-amber-400">
              暂无可用价格方案，请先到「套餐管理」启用套餐并配置价格方案
            </p>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">生成数量</label>
            <input v-model.number="createForm.totalCount" type="number" class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100" placeholder="输入生成数量" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">过期时间（可选）</label>
            <input v-model="createForm.expiresAt" type="datetime-local" class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">备注（可选）</label>
            <textarea v-model="createForm.remark" rows="3" class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100" placeholder="输入备注"></textarea>
          </div>
        </div>
      </template>
      <template #footer>
        <button @click="showCreateModal = false" class="cancel-btn">取消</button>
        <button @click="handleCreateBatch" :disabled="creating" class="primary-btn ml-3">
          {{ creating ? '创建中...' : '创建' }}
        </button>
      </template>
    </Modal>

    <!-- 批次详情 Modal -->
    <Modal
      :show="showDetailModal"
      @close="showDetailModal = false"
      modalSize="sm:my-8 sm:w-full sm:max-w-6xl sm:p-6"
    >
      <template #header>
        批次详情 - {{ currentBatch?.batchNo }}
      </template>
      <template #body>
        <div v-if="currentBatch" class="space-y-4">
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="text-sm font-medium text-gray-500 dark:text-gray-400">批次号</label>
            <p class="text-gray-900 dark:text-gray-100">{{ currentBatch.batchNo }}</p>
          </div>
          <div>
            <label class="text-sm font-medium text-gray-500 dark:text-gray-400">价格方案</label>
            <p class="text-gray-900 dark:text-gray-100">{{ getPricingTitle(currentBatch.pricingId) }}</p>
            <p v-if="getPricingSubtitle(currentBatch.pricingId)" class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {{ getPricingSubtitle(currentBatch.pricingId) }}
            </p>
          </div>
          <div>
            <label class="text-sm font-medium text-gray-500 dark:text-gray-400">生成数量</label>
            <p class="text-gray-900 dark:text-gray-100">{{ currentBatch.generatedCount }} / {{ currentBatch.totalCount }}</p>
          </div>
          <div>
            <label class="text-sm font-medium text-gray-500 dark:text-gray-400">过期时间</label>
            <p class="text-gray-900 dark:text-gray-100">{{ currentBatch.expiresAt ? formatDate(currentBatch.expiresAt) : '永久有效' }}</p>
          </div>
          <div>
            <label class="text-sm font-medium text-gray-500 dark:text-gray-400">状态</label>
            <p class="text-gray-900 dark:text-gray-100">{{ currentBatch.status === 'ACTIVE' ? '激活' : '禁用' }}</p>
          </div>
          <div>
            <label class="text-sm font-medium text-gray-500 dark:text-gray-400">创建时间</label>
            <p class="text-gray-900 dark:text-gray-100">{{ formatDate(currentBatch.createdAt) }}</p>
          </div>
        </div>

        <!-- 统计 -->
        <div class="grid grid-cols-4 gap-4 mt-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
          <div class="text-center">
            <p class="text-2xl font-bold text-gray-900 dark:text-gray-100">{{ currentStats?.total || 0 }}</p>
            <p class="text-sm text-gray-500 dark:text-gray-400">总数</p>
          </div>
          <div class="text-center">
            <p class="text-2xl font-bold text-green-600">{{ currentStats?.unused || 0 }}</p>
            <p class="text-sm text-gray-500 dark:text-gray-400">未使用</p>
          </div>
          <div class="text-center">
            <p class="text-2xl font-bold text-blue-600">{{ currentStats?.redeemed || 0 }}</p>
            <p class="text-sm text-gray-500 dark:text-gray-400">已兑换</p>
          </div>
          <div class="text-center">
            <p class="text-2xl font-bold text-red-600">{{ (currentStats?.disabled || 0) + (currentStats?.expired || 0) }}</p>
            <p class="text-sm text-gray-500 dark:text-gray-400">已失效</p>
          </div>
        </div>

        <!-- 兑换码列表 -->
        <div class="mt-4">
          <h4 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">兑换码列表</h4>
          <div class="max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
            <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead class="bg-gray-50 dark:bg-gray-900 sticky top-0">
                <tr>
                  <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">兑换码</th>
                  <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">状态</th>
                  <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">过期时间</th>
                  <th class="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400">操作</th>
                </tr>
              </thead>
              <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                <tr v-for="code in codes" :key="code.id">
                  <td class="px-4 py-2 text-sm font-mono text-gray-900 dark:text-gray-100">{{ code.code }}</td>
                  <td class="px-4 py-2 text-sm">
                    <span :class="getStatusClass(code.status)" class="px-2 py-0.5 rounded-full text-xs">{{ getStatusText(code.status) }}</span>
                  </td>
                  <td class="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">{{ code.expiresAt ? formatDate(code.expiresAt) : '-' }}</td>
                  <td class="px-4 py-2 text-right">
                    <button v-if="code.status === 'UNUSED'" @click="handleDisableCode(code.id)" class="text-red-600 hover:text-red-900 text-sm">禁用</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <!-- 码列表分页 -->
          <div v-if="codeTotal > 0" class="mt-2 flex justify-between items-center text-sm text-gray-500">
            <span>共 {{ codeTotal }} 条</span>
            <div class="flex gap-2">
              <button @click="loadCodes(codePage - 1)" :disabled="codePage === 0" class="px-2 py-1 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-50">上一页</button>
              <button @click="loadCodes(codePage + 1)" :disabled="(codePage + 1) * codePageSize >= codeTotal" class="px-2 py-1 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-50">下一页</button>
            </div>
          </div>
        </div>
      </div>
      </template>
    </Modal>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, reactive } from 'vue'
import Modal from '@/components/ui/Modal.vue'
import toaster from '@/utils/toaster'
import { getBatchListAPI, createBatchAPI, getBatchDetailAPI, getBatchCodesAPI, exportCodesAPI, disableCodeAPI } from '@/api/membership/redeem'
import { getAllPlansAPI } from '@/api/membership/plan'
import type { CreateBatchPayload } from '@/api/membership/redeem'
import type { Plan } from '@/types/membership/plan'
import { DurationTypeLabels } from '@/types/membership/enums'
import { displayLocalizedText } from '@/types/membership/localized'

interface Batch {
  id: number
  batchNo: string
  pricingId: number
  totalCount: number
  generatedCount: number
  expiresAt: string
  status: string
  remark: string
  createdBy: number
  createdAt: string
  updatedAt: string
}

interface Code {
  id: number
  batchId: number
  code: string
  status: string
  expiresAt: string
  redeemedBy: number | null
  redeemedAt: string | null
  subscriptionId: number | null
  createdAt: string
  updatedAt: string
}

interface PricingOption {
  pricingId: number
  planName: string
  durationType: keyof typeof DurationTypeLabels
  durationDays: number
  price: number
  isOneTime: boolean
  isAvailable: boolean
  planDisplayOrder: number
  pricingDisplayOrder: number
}

// 列表状态
const loading = ref(false)
const batches = ref<Batch[]>([])
const total = ref(0)
const page = ref(0)
const pageSize = 20
const batchStats = reactive<Record<number, { redeemed: number }>>({})
const pricingMap = reactive<Record<number, PricingOption>>({})
const selectablePricingOptions = ref<PricingOption[]>([])

// 创建批次
const showCreateModal = ref(false)
const creating = ref(false)
const createForm = reactive({
  pricingId: 0,
  totalCount: 0,
  expiresAt: '',
  remark: '',
})

// 详情
const showDetailModal = ref(false)
const currentBatch = ref<Batch | null>(null)
const currentStats = ref<{ total: number; unused: number; redeemed: number; disabled: number; expired: number } | null>(null)
const codes = ref<Code[]>([])
const codeTotal = ref(0)
const codePage = ref(0)
const codePageSize = 50

const formatPrice = (price: number) => `¥${(price / 100).toFixed(2)}`

const getPricingDurationLabel = (option: PricingOption) => {
  const durationTypeLabel = DurationTypeLabels[option.durationType] || option.durationType
  return `${durationTypeLabel}（${option.durationDays}天）`
}

const getPricingSubtitleByOption = (option: PricingOption) => {
  const parts = [getPricingDurationLabel(option), formatPrice(option.price)]
  if (option.isOneTime) {
    parts.push('一次性')
  }
  return parts.join(' · ')
}

const formatPricingOptionLabel = (option: PricingOption) => {
  return `${option.planName} · ${getPricingSubtitleByOption(option)}`
}

const getPricingTitle = (pricingId: number) => {
  const option = pricingMap[pricingId]
  if (!option) {
    return `价格方案 #${pricingId}`
  }
  return option.planName
}

const getPricingSubtitle = (pricingId: number) => {
  const option = pricingMap[pricingId]
  if (!option) {
    return ''
  }
  return getPricingSubtitleByOption(option)
}

const loadPricingOptions = async () => {
  try {
    const res = await getAllPlansAPI()
    if (res.status !== 200) {
      return
    }

    const plans: Plan[] = res.data?.plans || []
    const nextPricingMap: Record<number, PricingOption> = {}
    const nextSelectableOptions: PricingOption[] = []

    for (const plan of plans) {
      const planPricings = plan.pricings || []
      for (const pricing of planPricings) {
        const durationType = pricing.durationType as keyof typeof DurationTypeLabels
        const option: PricingOption = {
          pricingId: pricing.id,
          planName: displayLocalizedText(plan.name),
          durationType,
          durationDays: pricing.durationDays,
          price: pricing.price,
          isOneTime: pricing.isOneTime,
          isAvailable: plan.isActive && !plan.isHidden && !pricing.isHidden,
          planDisplayOrder: plan.displayOrder,
          pricingDisplayOrder: pricing.displayOrder,
        }

        nextPricingMap[pricing.id] = option

        if (option.isAvailable) {
          nextSelectableOptions.push(option)
        }
      }
    }

    Object.keys(pricingMap).forEach((key) => {
      delete pricingMap[Number(key)]
    })
    Object.assign(pricingMap, nextPricingMap)

    selectablePricingOptions.value = nextSelectableOptions.sort((a, b) => {
      if (a.planDisplayOrder !== b.planDisplayOrder) {
        return a.planDisplayOrder - b.planDisplayOrder
      }
      if (a.pricingDisplayOrder !== b.pricingDisplayOrder) {
        return a.pricingDisplayOrder - b.pricingDisplayOrder
      }
      return a.pricingId - b.pricingId
    })

    if (
      createForm.pricingId &&
      !selectablePricingOptions.value.some((option) => option.pricingId === createForm.pricingId)
    ) {
      createForm.pricingId = 0
    }
  } catch (error) {
    console.error('加载价格方案选项失败:', error)
    toaster.error('加载价格方案失败，请稍后重试')
  }
}

const openCreateModal = async () => {
  showCreateModal.value = true
  await loadPricingOptions()
}

// 加载批次列表
const loadBatches = async (pageNum: number = 0) => {
  loading.value = true
  try {
    const res = await getBatchListAPI(pageNum * pageSize, pageSize)
    if (res.status === 200) {
      batches.value = res.data.batches
      total.value = res.data.total
      page.value = pageNum

      // 加载每个批次的统计
      for (const batch of batches.value) {
        loadBatchStats(batch.id)
      }
    }
  } catch (error) {
    console.error('加载批次列表失败:', error)
  } finally {
    loading.value = false
  }
}

// 加载批次统计
const loadBatchStats = async (batchId: number) => {
  try {
    const res = await getBatchDetailAPI(batchId)
    if (res.status === 200) {
      batchStats[batchId] = {
        redeemed: res.data.stats.redeemed,
      }
    }
  } catch (error) {
    console.error('加载批次统计失败:', error)
  }
}

// 创建批次
const handleCreateBatch = async () => {
  if (!createForm.pricingId || !createForm.totalCount) {
    toaster.warning('请选择套餐与时长，并填写生成数量')
    return
  }

  creating.value = true
  try {
    const data: CreateBatchPayload = {
      pricingId: createForm.pricingId,
      totalCount: createForm.totalCount,
    }
    if (createForm.expiresAt) {
      data.expiresAt = new Date(createForm.expiresAt).toISOString()
    }
    if (createForm.remark) {
      data.remark = createForm.remark
    }

    const res = await createBatchAPI(data)
    if (res.status === 200) {
      toaster.success('创建成功')
      showCreateModal.value = false
      loadBatches()
      // 重置表单
      createForm.pricingId = 0
      createForm.totalCount = 0
      createForm.expiresAt = ''
      createForm.remark = ''
    } else {
      toaster.error(res.message || '创建失败')
    }
  } catch (error: any) {
    toaster.error(error.message || '创建失败')
  } finally {
    creating.value = false
  }
}

// 查看批次详情
const viewBatchDetail = async (batch: Batch) => {
  currentBatch.value = batch
  showDetailModal.value = true
  codePage.value = 0

  try {
    const res = await getBatchDetailAPI(batch.id)
    if (res.status === 200) {
      currentStats.value = res.data.stats
    }
    await loadCodes(0)
  } catch (error) {
    console.error('加载批次详情失败:', error)
  }
}

// 加载兑换码列表
const loadCodes = async (pageNum: number = 0) => {
  if (!currentBatch.value) return

  try {
    const res = await getBatchCodesAPI(currentBatch.value.id, pageNum * codePageSize, codePageSize)
    if (res.status === 200) {
      codes.value = res.data.codes
      codeTotal.value = res.data.total
      codePage.value = pageNum
    }
  } catch (error) {
    console.error('加载兑换码列表失败:', error)
  }
}

// 禁用兑换码
const handleDisableCode = async (codeId: number) => {
  try {
    const res = await disableCodeAPI(codeId)
    if (res.status === 200) {
      toaster.success('禁用成功')
      loadCodes(codePage.value)
      // 刷新统计
      if (currentBatch.value) {
        const detailRes = await getBatchDetailAPI(currentBatch.value.id)
        if (detailRes.status === 200) {
          currentStats.value = detailRes.data.stats
        }
      }
    } else {
      toaster.error(res.message || '禁用失败')
    }
  } catch (error: any) {
    toaster.error(error.message || '禁用失败')
  }
}

// 导出
const exportBatch = async (batch: Batch, format: string) => {
  try {
    const blob = await exportCodesAPI(batch.id, format)

    const isJsonErrorBlob = blob.type.includes('application/json') || blob.type.includes('text/json')
    if (isJsonErrorBlob) {
      let message = '导出失败'
      try {
        const errorText = await blob.text()
        if (errorText) {
          const errorData = JSON.parse(errorText) as { message?: string }
          message = errorData.message || message
        }
      } catch (parseError) {
        console.error('解析导出错误响应失败:', parseError)
      }
      toaster.error(message)
      return
    }

    if (blob.size === 0) {
      toaster.error('导出失败，返回空文件')
      return
    }

    // 创建下载
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `redeem-codes-${batch.batchNo}.${format}`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
    toaster.success('导出成功')
  } catch (error) {
    console.error('导出失败:', error)
    toaster.error('导出失败')
  }
}

// 工具函数
const formatDate = (dateStr: string) => {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleString('zh-CN')
}

const getStatusClass = (status: string) => {
  const classes: Record<string, string> = {
    UNUSED: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    REDEEMED: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    EXPIRED: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    DISABLED: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  }
  return classes[status] || classes.UNUSED
}

const getStatusText = (status: string) => {
  const texts: Record<string, string> = {
    UNUSED: '未使用',
    REDEEMED: '已兑换',
    EXPIRED: '已过期',
    DISABLED: '已禁用',
  }
  return texts[status] || status
}

onMounted(() => {
  loadPricingOptions()
  loadBatches()
})
</script>
